import {useStoreUnsafe} from '@/store';
import {useAuth0} from '@auth0/auth0-react';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {setCheckoutToken, setAccessToken as setTRPCAccessToken} from '../api/trpcClient';
import {AsyncState} from '../types/AsyncState';
import {useUrlToken} from './useUrlToken';

interface DecodedTokenPayload {
  permissions?: string[];
  aud?: string[];
  [key: string]: unknown;
}

export const useAccessToken = () => {
  const {isAuthenticated, getAccessTokenSilently, user, isLoading} = useAuth0();
  const {setAccessToken: setStoreAccessToken, setAuthProfile} = useStoreUnsafe();
  const {token: checkoutToken} = useUrlToken({
    name: 's',
    type: 'hash',
  });
  const location = useLocation();
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState<AsyncState<string | undefined>>({
    isLoading: true,
    isError: false,
  });

  useEffect(() => {
    if (!user || isLoading) {
      setAuthProfile({isLoading, data: undefined, hasPermission: () => false});
      return;
    }

    setAuthProfile({
      isLoading: false,
      data: {
        email: user.email,
        name: user.name,
        nickname: user.nickname,
        picture: user.picture,
        sub: user.sub,
        permissions: [], // Initialize with empty array, will be populated in token effect
      },
      hasPermission: () => false, // Initially false, will be updated with token,
    });
  }, [user, isLoading]);

  useEffect(() => {
    // During the checkout session, the TRPC context is set by the checkout id
    // and not from optionally logged in user. Therefore, we have to exclude access tokens.
    // There might be a left over checkout token from a previous session, therefore we need to include it for checkout path.
    if (checkoutToken && location.pathname.includes('checkout')) {
      setCheckoutToken(checkoutToken);
      setAccessToken({isLoading: false, isError: false, data: undefined});
      return;
    }

    if (isAuthenticated) {
      getAccessTokenSilently()
        .then(token => {
          // Extract permissions from the token
          const decodedToken = jwtDecode<DecodedTokenPayload>(token);
          const permissions = decodedToken.permissions || [];

          if (!decodedToken?.aud?.includes(import.meta.env.VITE_AUTH0_PORTAL_AUDIENCE)) {
            setTimeout(() => {
              navigate('/logout');
            }, 1000);
          }

          // Update auth profile with permissions
          setAuthProfile(prev => ({
            ...prev,
            data: prev.data
              ? {
                  ...prev.data,
                  permissions,
                }
              : undefined,
            hasPermission: permission => permissions.includes(permission),
          }));

          setStoreAccessToken?.(token);
          setTRPCAccessToken(token);
          setAccessToken({isLoading: false, isError: false, data: token});
        })
        .catch(error => {
          console.error('Error getting access token:', error);
          setAccessToken({
            isLoading: false,
            isError: true,
            error: {code: error.code, message: error.message},
          });
        });
    } else {
      setAccessToken({isLoading: false, isError: false, data: undefined});
    }
    // location.pathname is intentionally not included in the dependency array because we don't want
    // to update the token every time the page changes.
  }, [isAuthenticated, getAccessTokenSilently, checkoutToken]);

  return {accessToken};
};
